import React, { useState, useEffect } from 'react';
import FichaDialog from './ui/FichaDialog';
import Button from './ui/Button';
import EstadoOptimizacionFoto from './EstadoOptimizacionFoto';
import { AjustadorEncuadre, cargarImagen, detectarColorDeFondo } from './ProductImageSelector';
import { fetchMediaArchivoDataUrl, optimizarFoto } from '../utils/productApi';
import { procesarImagenParaSubir } from '../utils/productImages';
import { encuadreRatio } from '../utils/imagenAjuste45';
import { payloadEncuadre } from '../utils/encuadreFoto';
import './EditarEncuadreModal.css';

// IA-1/P3 (editar encuadre): re-encuadra una foto YA montada. Trae la foto por el proxy same-origin,
// muestra el MISMO editor (acercar/alejar/mover) en la proporción del tipo, y al aplicar sube por la
// tubería (JPG, nombre SEO, alt conservado), reasigna en el mismo lugar como la optimización y registra
// FotoOptimizada con origen 'encuadre' (vieja conservada, revertible). No bloquea el formulario.
const PROP_DEFAULT = { ratioW: 4, ratioH: 5, maxW: 1600, maxH: 2000, label: '4:5' };
const PASOS = [{ k: 'encuadrando', t: 'Encuadrando…' }, { k: 'subiendo', t: 'Subiendo a la tienda…' }, { k: 'asignando', t: 'Asignando' }];

// Vista de carga (presentacional, testeable): vista previa atenuada + pasos + el MISMO componente de
// estado que las celdas de variación (spinner / error + Reintentar). Botones deshabilitados al cargar.
export function VistaAplicandoEncuadre({ dataUrl, paso, error = null, onReintentar, onCancelar }) {
  const idx = PASOS.findIndex((p) => p.k === paso);
  return (
    <div className="ee-aplicando">
      <div className="ee-preview-dim">{dataUrl && <img src={dataUrl} alt="Encuadre en proceso" />}</div>
      <ol className="ee-pasos" aria-label="Progreso del encuadre">
        {PASOS.map((p, i) => (
          <li key={p.k} className={`ee-paso${!error && i < idx ? ' ee-paso--ok' : ''}${!error && i === idx ? ' ee-paso--run' : ''}`}>
            <span aria-hidden="true">{!error && i < idx ? '✓ ' : !error && i === idx ? '• ' : '· '}</span>{p.t}
          </li>
        ))}
      </ol>
      <EstadoOptimizacionFoto est={error ? { estado: 'error', error } : { estado: 'optimizando' }} onReintentar={onReintentar} />
      <div className="ee-acc"><Button size="sm" onClick={onCancelar} disabled={!error}>{error ? 'Cerrar' : 'Procesando…'}</Button></div>
    </div>
  );
}

export default function EditarEncuadreModal({ productId, nombre, variante = '', mediaId, src, alt = '', etiqueta = '', proporcion = PROP_DEFAULT, onDone, onClose }) {
  const [item, setItem] = useState(null);         // { dataUrl, dims, editor }
  const [cargando, setCargando] = useState(true);
  const [estado, setEstado] = useState('editando'); // 'editando' | 'aplicando' | 'error'
  const [paso, setPaso] = useState(null);
  const [error, setError] = useState('');

  useEffect(() => {
    let vivo = true;
    (async () => {
      try {
        const dataUrl = await fetchMediaArchivoDataUrl(mediaId);
        const img = await cargarImagen(dataUrl);
        const color = await detectarColorDeFondo(dataUrl);
        if (!vivo) return;
        setItem({ dataUrl, dims: { width: img.naturalWidth, height: img.naturalHeight }, editor: { zoom: 1, cx: 0.5, cy: 0.5, color } });
      } catch (e) { if (vivo) setError(`No se pudo traer la foto: ${e.message}`); }
      finally { if (vivo) setCargando(false); }
    })();
    return () => { vivo = false; };
  }, [mediaId]);

  const aplicar = async () => {
    if (!item || !item.editor) return;
    setEstado('aplicando'); setPaso('encuadrando'); setError('');
    try {
      const img = await cargarImagen(item.dataUrl);
      const e = encuadreRatio(item.dims.width, item.dims.height, { ...item.editor, ratioW: proporcion.ratioW, ratioH: proporcion.ratioH, maxW: proporcion.maxW, maxH: proporcion.maxH });
      const c = document.createElement('canvas'); c.width = e.canvasW; c.height = e.canvasH;
      const ctx = c.getContext && c.getContext('2d');
      if (!ctx) throw new Error('el navegador no dio canvas');
      ctx.fillStyle = item.editor.color || '#ffffff'; ctx.fillRect(0, 0, e.canvasW, e.canvasH);
      ctx.drawImage(img, e.dx, e.dy, e.dw, e.dh);
      setPaso('subiendo');
      const finalUrl = await procesarImagenParaSubir(c.toDataURL('image/png'), { maxW: proporcion.maxW, maxH: proporcion.maxH });
      const imageBase64 = finalUrl.split(',')[1];
      const loteId = `encuadre-${mediaId}-${Date.now()}`;
      const res = await optimizarFoto(payloadEncuadre({ imageBase64, nombre, variante, mediaId, src, alt, etiqueta, productId, loteId }));
      setPaso('asignando');
      onDone({ ...res, loteId, alt });   // el padre cierra el diálogo y muestra la foto nueva + confirmación
    } catch (err) { setError(`No se pudo aplicar el encuadre: ${err.message || 'error del navegador'}.`); setEstado('error'); }
  };

  return (
    <FichaDialog titulo="Editar encuadre" ariaLabel="Editar el encuadre de la foto" onClose={estado === 'aplicando' ? () => {} : onClose} className="pis">
      {cargando && <p className="pis-muted">Trayendo la foto…</p>}
      {!cargando && error && estado !== 'error' && <div className="pis-error">{error}</div>}
      {item && estado === 'editando' && (
        <AjustadorEncuadre
          item={item}
          prop={proporcion}
          onCambiar={(patch) => setItem((p) => ({ ...p, editor: { ...p.editor, ...patch } }))}
          onCancelar={onClose}
          onAplicar={aplicar}
        />
      )}
      {item && (estado === 'aplicando' || estado === 'error') && (
        <VistaAplicandoEncuadre
          dataUrl={item.dataUrl}
          paso={paso}
          error={estado === 'error' ? error : null}
          onReintentar={() => { setEstado('editando'); setError(''); }}
          onCancelar={onClose}
        />
      )}
    </FichaDialog>
  );
}
